import React, { CSSProperties, useMemo } from 'react';
import { Pie, PieCustomLayerProps } from '@nivo/pie';
import { ResponseCount } from './room-data-helpers';

import './pie.css';

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const DONUT_SIZE = 260;

// Slice colours, assigned by popularity (most popular first). nivo needs
// real colour values rather than CSS variables, so these mirror the palette
// in global.css. `text` is the readable label colour on top of each fill.
const SLICE_COLORS = [
  { fill: '#38b6f5', text: '#142a66' }, // --blue
  { fill: '#f06ea3', text: '#ffffff' }, // --pink
  { fill: '#17357f', text: '#ffffff' }, // --navy
  { fill: '#7fd2f7', text: '#142a66' }, // --sky-strong
  { fill: '#f6b3cd', text: '#142a66' }, // --pink-soft
  { fill: '#a7b4d3', text: '#142a66' }, // --disabled
];
const EMPTY_COLOR = { fill: '#dbeafb', text: '#142a66' }; // --sky-soft

type SliceDatum = {
  id: string;
  label: string;
  value: number;
  color: string;
  textColor: string;
};

type LegendRow = {
  text: string;
  letter: string;
  count: number;
  percent: number;
  color: string;
  textColor: string;
  isWinner: boolean;
  isGuess: boolean;
};

export type PieProps = {
  // Tallied answers for the current prompt (only choices somebody picked).
  data: ResponseCount[];
  // The prompt's choices in display order; provides the A/B/C letters and
  // lets the legend list options nobody picked.
  choices?: string[];
  // The most popular choice(s); more than one on a tie.
  correctAnswers?: string[];
  // What the active player guessed, and who they are.
  guess?: string;
  guesserName?: string;
  question?: string;
};

const buildRows = (
  data: ResponseCount[],
  choices: string[] | undefined,
  correctAnswers: string[],
  guess: string | undefined
): LegendRow[] => {
  const countByText: Record<string, number> = {};
  data.forEach(({ text, count }) => {
    countByText[text] = (countByText[text] ?? 0) + count;
  });

  // Prefer the prompt's own choice order so letters line up with the
  // option cards players just saw; fall back to whatever was answered.
  const ordered = choices && choices.length > 0
    ? [...choices, ...data.map(d => d.text).filter(t => !choices.includes(t))]
    : data.map(d => d.text);

  const total = Object.values(countByText).reduce((sum, c) => sum + c, 0) || 1;

  const rows = ordered.map((text, i) => ({
    text,
    letter: LETTERS[i % LETTERS.length],
    count: countByText[text] ?? 0,
    percent: Math.round(((countByText[text] ?? 0) / total) * 100),
    isWinner: correctAnswers.includes(text),
    isGuess: guess === text,
    order: i,
  }));

  // Most popular first, ties keep the choice order.
  rows.sort((a, b) => (b.count - a.count) || (a.order - b.order));

  let colorIndex = 0;
  return rows.map(({ text, letter, count, percent, isWinner, isGuess }) => {
    const color = count > 0
      ? SLICE_COLORS[Math.min(colorIndex++, SLICE_COLORS.length - 1)]
      : EMPTY_COLOR;
    return { text, letter, count, percent, isWinner, isGuess, color: color.fill, textColor: color.text };
  });
};

// Total answer count in the donut hole, drawn as a custom nivo layer.
const makeCenterLayer = (total: number) => {
  const CenterTotal = ({ centerX, centerY }: PieCustomLayerProps<SliceDatum>) => (
    <text
      className='pieCenter'
      x={centerX}
      y={centerY}
      textAnchor='middle'
      dominantBaseline='central'
    >
      <tspan className='pieCenterNumber' x={centerX} dy='-0.25em'>{total}</tspan>
      <tspan className='pieCenterLabel' x={centerX} dy='1.8em'>{total === 1 ? 'answer' : 'answers'}</tspan>
    </text>
  );
  return CenterTotal;
};

export const PieChart = ({
  data,
  choices,
  correctAnswers = [],
  guess,
  guesserName,
  question
}: PieProps) => {
  const rows = useMemo(
    () => buildRows(data, choices, correctAnswers, guess),
    [data, choices, correctAnswers, guess]
  );

  const total = useMemo(() => rows.reduce((sum, r) => sum + r.count, 0), [rows]);

  const slices: SliceDatum[] = useMemo(() => rows
    .filter(r => r.count > 0)
    .map(r => ({
      id: r.letter,
      label: r.text,
      value: r.count,
      color: r.color,
      textColor: r.textColor
    })), [rows]);

  const centerLayer = useMemo(() => makeCenterLayer(total), [total]);

  return (
    <div className='pieCard'>
      {question && (
        <>
          <div className='pieKicker'>How everyone answered</div>
          <div className='pieQuestion'>{question}</div>
        </>
      )}
      <div className='pieBody'>
        <div className='pieDonut'>
          <Pie<SliceDatum>
            width={DONUT_SIZE}
            height={DONUT_SIZE}
            data={slices}
            theme={{
              text: {
                fontSize: 18,
                fontFamily: 'var(--font-heading)',
                fontWeight: 600
              }
            }}
            motionConfig='wobbly'
            isInteractive={false}
            sortByValue={true}
            innerRadius={0.55}
            padAngle={2}
            cornerRadius={5}
            borderWidth={0}
            colors={d => d.data.color}
            enableArcLinkLabels={false}
            arcLabel={d => `${Math.round((d.value / (total || 1)) * 100)}%`}
            arcLabelsSkipAngle={28}
            arcLabelsTextColor={d => d.data.textColor}
            layers={['arcs', 'arcLabels', centerLayer]}
          />
        </div>
        <ul className='pieLegend'>
          {rows.map((row, i) => (
            <li
              key={row.text}
              className={[
                'pieLegendRow',
                row.isWinner ? 'winner' : '',
                row.count === 0 ? 'empty' : ''
              ].filter(Boolean).join(' ')}
              style={{ '--i': i } as CSSProperties}
            >
              <span
                className='pieBadge'
                style={{ background: row.color, color: row.textColor }}
              >
                {row.letter}
              </span>
              <span className='pieLegendText'>{row.text}</span>
              {row.isWinner && <span className='pieTag win'>Most popular</span>}
              {row.isGuess && guesserName && (
                <span className='pieTag'>{`${guesserName}'s pick`}</span>
              )}
              <span className='pieLegendCount'>{row.count}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
